import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { useFacilitatorStore } from '../useFacilitatorStore';

const isUnnamedFacilitator = (facilitator) => {
  const name = facilitator.name?.trim().toLowerCase();
  return !name || name === 'unassigned';
};

const getFacilitatorGroups = (groups, facilitatorId) =>
  groups.filter((group) => group.facilitatorIds.includes(facilitatorId));

const formatCurrentGroups = (groups, facilitatorId) => {
  const facilitatorGroups = getFacilitatorGroups(groups, facilitatorId);

  return facilitatorGroups.length
    ? facilitatorGroups.map((group) => `Group ${group.name}`).join(', ')
    : 'None';
};

const EditFacilitatorInfoPage = () => {
  const data = useFacilitatorStore((state) => state.data);
  const addFacilitator = useFacilitatorStore((state) => state.addFacilitator);
  const [facilitatorNames, setFacilitatorNames] = useState(() =>
    Object.fromEntries(
      data.facilitators.map((facilitator) => [
        facilitator.id,
        isUnnamedFacilitator(facilitator) ? '' : facilitator.name,
      ]),
    ),
  );
  const [errors, setErrors] = useState({});
  const [savedFacilitatorId, setSavedFacilitatorId] = useState('');

  const namedFacilitators = useMemo(
    () =>
      data.facilitators.filter(
        (facilitator) => !isUnnamedFacilitator(facilitator),
      ),
    [data.facilitators],
  );

  const unnamedFacilitators = useMemo(
    () => data.facilitators.filter(isUnnamedFacilitator),
    [data.facilitators],
  );

  const handleNameChange = (facilitatorId, value) => {
    setFacilitatorNames((prev) => ({ ...prev, [facilitatorId]: value }));
    setErrors((prev) => ({ ...prev, [facilitatorId]: '' }));
    setSavedFacilitatorId('');
  };

  const handleSaveFacilitator = (facilitatorId) => {
    const name = facilitatorNames[facilitatorId]?.trim();

    if (!name) {
      setErrors((prev) => ({
        ...prev,
        [facilitatorId]: 'Facilitator name is required.',
      }));
      return;
    }

    addFacilitator(facilitatorId, name);
    setErrors((prev) => ({ ...prev, [facilitatorId]: '' }));
    setSavedFacilitatorId(facilitatorId);
  };

  return (
    <div className='text-left'>
      <AdminHeader />
      <main className='p-4 max-w-5xl mx-auto'>
        <div className='my-3'>
          <Link
            className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to='/facilitator/dashboard'
          >
            &larr; Go Back
          </Link>
        </div>

        <h1 className='my-5 text-3xl font-bold text-dark-blue'>
          Edit Facilitator Info
        </h1>

        <div className='flex flex-col gap-5 text-sm'>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 1
            </div>
            <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue mb-1'>
              Name Unassigned Facilitators
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Add names for facilitator IDs that have not been named yet. Once a
              name is saved, that facilitator will move into the named list.
            </p>

            <div className='flex flex-col gap-2'>
              {unnamedFacilitators.length ? (
                unnamedFacilitators.map((facilitator) => (
                  <div
                    key={facilitator.id}
                    className='flex max-w-2xl flex-wrap items-center gap-2 border border-gray-200 bg-white rounded p-2.5'
                  >
                    <input
                      type='text'
                      value={facilitatorNames[facilitator.id] || ''}
                      onChange={(event) =>
                        handleNameChange(facilitator.id, event.target.value)
                      }
                      placeholder='Facilitator name'
                      className='w-56 max-w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm'
                    />
                    <span className='font-bold text-sm'>
                      ID: {facilitator.id}
                    </span>
                    <button
                      type='button'
                      onClick={() => handleSaveFacilitator(facilitator.id)}
                      className='ml-auto bg-light-blue text-white px-3 py-1.5 rounded font-bold hover:bg-blue-600 transition-colors duration-200'
                    >
                      Save Name
                    </button>
                    {errors[facilitator.id] && (
                      <span className='w-full text-xs font-bold text-red-600'>
                        {errors[facilitator.id]}
                      </span>
                    )}
                    {savedFacilitatorId === facilitator.id && (
                      <span className='w-full text-xs font-bold text-green'>
                        Saved
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <p>All facilitators have names.</p>
              )}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 2
            </div>
            <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue mb-1'>
              Edit Named Facilitators
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Update facilitator names here. Current group assignments are shown
              under each facilitator so you can confirm who is attached to which
              group.
            </p>

            <div className='flex max-w-2xl flex-col gap-2'>
              {namedFacilitators.length ? (
                namedFacilitators.map((facilitator) => (
                  <div
                    key={facilitator.id}
                    className='flex flex-wrap items-start gap-2 border border-gray-200 bg-white rounded p-2.5'
                  >
                    <div className='flex min-w-[260px] flex-1 flex-col gap-1'>
                      <label
                        className='font-bold text-sm'
                        htmlFor={`facilitator-${facilitator.id}`}
                      >
                        Facilitator Name
                      </label>
                      <input
                        id={`facilitator-${facilitator.id}`}
                        type='text'
                        value={facilitatorNames[facilitator.id] || ''}
                        onChange={(event) =>
                          handleNameChange(facilitator.id, event.target.value)
                        }
                        className='w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm'
                      />
                      <span className='text-xs text-gray-700'>
                        Current Groups:{' '}
                        {formatCurrentGroups(data.groups, facilitator.id)}
                      </span>
                      {errors[facilitator.id] && (
                        <span className='text-xs font-bold text-red-600'>
                          {errors[facilitator.id]}
                        </span>
                      )}
                      {savedFacilitatorId === facilitator.id && (
                        <span className='text-xs font-bold text-green'>
                          Saved
                        </span>
                      )}
                    </div>
                    <span className='pt-8 font-bold text-sm'>
                      ID: {facilitator.id}
                    </span>
                    <button
                      type='button'
                      onClick={() => handleSaveFacilitator(facilitator.id)}
                      className='mt-6 bg-light-blue text-white px-3 py-1.5 rounded font-bold hover:bg-blue-600 transition-colors duration-200'
                    >
                      Save
                    </button>
                  </div>
                ))
              ) : (
                <p>No named facilitators are available.</p>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default EditFacilitatorInfoPage;
